import http from "./http";
import { toSearchParams } from "../utils/queryHelper";
import type {
  Customer,
  CustomerListItem,
  CustomerGroup,
  CustomerActivity,
  CustomerTag,
  CustomerFilters,
  CustomerSearchResult,
  VadeIndirimResult,
  MatchSuggestion,
  TeslimatAdresi,
  FollowUpListResponse,
  CloseFollowUpPayload,
} from "../types/customer";

const API = "/api/customers";

// ─── Müşteri CRUD ───────────────────────────────────────────────────

export const getCustomers = (filters: CustomerFilters & { page?: number; limit?: number } = {}) => {
  const params = toSearchParams(filters as Record<string, unknown>);
  return http.get<{ customers: CustomerListItem[]; total: number; totalPages: number }>(
    `${API}?${params.toString()}`
  );
};

export const getCustomer = (id: number | string) =>
  http.get<Customer>(`${API}/${id}`);

export const searchCustomers = (q: string, limit = 10) =>
  http.get<CustomerSearchResult[]>(`${API}/search?q=${encodeURIComponent(q)}&limit=${limit}`);

export const createCustomer = (data: Partial<Customer>) =>
  http.post<Customer>(API, data);

export const updateCustomer = (id: number | string, data: Partial<Customer>) =>
  http.put<Customer>(`${API}/${id}`, data);

export const deleteCustomer = (id: number | string) =>
  http.delete(`${API}/${id}`);

// ─── Bağlı Veriler ─────────────────────────────────────────────────

export const getCustomerOrders = (id: number | string, page = 1, limit = 10) =>
  http.get<{ orders: Array<Record<string, unknown>>; total: number }>(
    `${API}/${id}/orders?page=${page}&limit=${limit}`
  );

export const syncOrdersWithCustomer = (id: number | string) =>
  http.put<{ updatedCount: number }>(`${API}/${id}/sync-orders`);

export const getCustomerActivities = (id: number | string, page = 1, limit = 20, aktivite_turu?: string) => {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (aktivite_turu) params.set('aktivite_turu', aktivite_turu);
  return http.get<{ activities: CustomerActivity[]; total: number }>(
    `${API}/${id}/activities?${params.toString()}`
  );
};

export const createActivity = (customerId: number | string, data: Partial<CustomerActivity> & { urun_ids?: number[] }) =>
  http.post<CustomerActivity>(`${API}/${customerId}/activities`, data);

export const updateActivity = (customerId: number | string, activityId: number, data: Partial<CustomerActivity> & { urun_ids?: number[] }) =>
  http.put<CustomerActivity>(`${API}/${customerId}/activities/${activityId}`, data);

export const deleteActivity = (customerId: number | string, activityId: number) =>
  http.delete(`${API}/${customerId}/activities/${activityId}`);

export const getCustomerTags = (id: number | string) =>
  http.get<CustomerTag[]>(`${API}/${id}/tags`);

export const assignTag = (customerId: number | string, etiketId: number) =>
  http.post(`${API}/${customerId}/tags`, { etiket_id: etiketId });

export const removeTag = (customerId: number | string, tagId: number) =>
  http.delete(`${API}/${customerId}/tags/${tagId}`);

// ─── Vade/İndirim ──────────────────────────────────────────────────

export const getVadeIndirim = (id: number | string) =>
  http.get<VadeIndirimResult>(`${API}/${id}/vade-indirim`);

// ─── Birleştirme ────────────────────────────────────────────────────

export const mergeCustomers = (hedefId: number, kaynakId: number) =>
  http.post<Customer>(`${API}/merge`, { hedef_id: hedefId, kaynak_id: kaynakId });

// ─── Eşleştirme Önerileri ───────────────────────────────────────────

export const getMatchSuggestionsCount = () =>
  http.get<{ count: number }>(`${API}/match-suggestions/count`);

export const getMatchSuggestions = (params: { page?: number; limit?: number; durum?: string } = {}) => {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.limit) search.set('limit', String(params.limit));
  if (params.durum) search.set('durum', params.durum);
  return http.get<{ suggestions: MatchSuggestion[]; total: number }>(
    `${API}/match-suggestions?${search.toString()}`
  );
};

export const approveMatch = (id: number, hedefId: number) =>
  http.post<{ hedef_id: number; kaynak_id: number }>(`${API}/match-suggestions/${id}/approve`, { hedef_id: hedefId });

export const rejectMatch = (id: number) =>
  http.post(`${API}/match-suggestions/${id}/reject`);

// ─── Müşteri Grupları ───────────────────────────────────────────────

export const getGroups = (includeInactive = false) =>
  http.get<CustomerGroup[]>(`${API}/groups?includeInactive=${includeInactive}`);

export const getGroup = (id: number | string) =>
  http.get<CustomerGroup>(`${API}/groups/${id}`);

export const createGroup = (data: Partial<CustomerGroup>) =>
  http.post<CustomerGroup>(`${API}/groups`, data);

export const updateGroup = (id: number | string, data: Partial<CustomerGroup>) =>
  http.put<CustomerGroup>(`${API}/groups/${id}`, data);

export const deleteGroup = (id: number | string) =>
  http.delete(`${API}/groups/${id}`);

export const getGroupOrders = (id: number | string, page = 1, limit = 20) =>
  http.get<{ orders: Array<Record<string, unknown>>; total: number }>(
    `${API}/groups/${id}/orders?page=${page}&limit=${limit}`
  );

export const getGroupStats = (id: number | string) =>
  http.get<{ siparis_adedi: number; toplam_ciro: number }>(`${API}/groups/${id}/stats`);

// ─── Teslimat Adresleri ──────────────────────────────────────────────

export const getCustomerAddresses = (id: number | string) =>
  http.get<TeslimatAdresi[]>(`${API}/${id}/addresses`);

export const saveCustomerAddresses = (id: number | string, addresses: TeslimatAdresi[]) =>
  http.put<TeslimatAdresi[]>(`${API}/${id}/addresses`, { addresses });

// ─── Alt Müşteriler ─────────────────────────────────────────────────

export const getChildren = (parentId: number | string) =>
  http.get<Array<{ id: number; ad: string | null; soyad: string | null; sirket_adi: string | null; telefon: string | null; email: string | null; son_teslimat_il: string | null; toplam_siparis_adedi: number; toplam_ciro: number; lead_durum: string }>>(`${API}/${parentId}/children`);

// ─── Etiket Tanımları ───────────────────────────────────────────────

export const getTags = () =>
  http.get<CustomerTag[]>(`${API}/tags`);

export const createTagDef = (data: Partial<CustomerTag>) =>
  http.post<CustomerTag>(`${API}/tags`, data);

export const updateTagDef = (id: number | string, data: Partial<CustomerTag>) =>
  http.put<CustomerTag>(`${API}/tags/${id}`, data);

export const deleteTagDef = (id: number | string) =>
  http.delete(`${API}/tags/${id}`);

// ─── Takip Hatırlatma ──────────────────────────────────────────────

export const getFollowUps = () =>
  http.get<FollowUpListResponse>(`${API}/follow-ups`);

export const getFollowUpsCount = () =>
  http.get<{ count: number }>(`${API}/follow-ups/count`);

export const closeFollowUp = (activityId: number, payload: CloseFollowUpPayload) =>
  http.post(`${API}/follow-ups/${activityId}/close`, payload);

export const snoozeFollowUp = (activityId: number, newDate: string) =>
  http.patch(`${API}/follow-ups/${activityId}/snooze`, { sonraki_adim_tarihi: newDate });
